import React, { useState } from 'react';
import {
	Box,
	Button,
	Flex,
	Image,
	Input,
	InputGroup,
	InputLeftElement,
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Spinner,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import { IconAvatar } from '../../../components/IconAvatar/iconAvatar';
import { CloseIcon } from '@chakra-ui/icons';
import { XoctagonIcon } from '../../../utils/icons/xoctagon';
import { LinkIcon } from '../../../utils/icons/link';
import { useReflink } from '../../../providers/Reflink/useReflink';
import { useUserEditorProvider } from '../../../providers/UserEditor/userEditorProvider';
import { t } from 'i18next';
import { useUserProvider } from '../../../providers/User/userProvider';
import { contrastColors } from '../../../utils/parser';
import { Color } from '../../../theme/color';

export const EditAccountIcon = ({ isOwner }: { isOwner: boolean }) => {
	const { data } = useUserProvider();

	const { isOpen, onOpen, onClose } = useDisclosure();
	const [ico, setIco] = useState(data.avatar);
	const [loadingIco, setIsLoadingIco] = useState(false);
	const [errIco, setIsErrIco] = useState(false);
	const [savingChanges, setIsSavingChanges] = useState(false);
	const editorUserData = useUserEditorProvider();
	const { getReflink } = useReflink();

	const handleClose = () => {
		editorUserData.setAvatar(ico);
		onClose();
	};

	const onInputChange = (event: any) => {
		setIsErrIco(false);
		setIsLoadingIco(true);
		setIco(event.target.value);
	};

	const onOpenChange = () => {
		if (!isOwner) {
			return;
		}

		setIco(editorUserData.avatar ?? data.avatar);
		onOpen();
	};

	const onErr = () => {
		setIsErrIco(true);
		setIsLoadingIco(false);
	};

	const onLoad = () => {
		setIsErrIco(false);
		setIsLoadingIco(false);
	};

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
			>
				<ModalOverlay />
				<ModalContent
					bg={Color.BACKGROUND_LIGHT}
					border={`1px solid ${Color.BACKGROUND_TERTIARY}`}
					m="0 20px"
					color="black"
					alignSelf="center"
				>
					<ModalHeader>
						<Flex
							display="flex"
							justifyContent="space-between"
							alignItems="center"
							color="white"
						>
							<Text
								fontWeight="600"
								fontSize="20px"
							>
								{t('user.addUrl').toString()}
							</Text>
							<Flex
								cursor="pointer"
								justifyContent="center"
								maxW="20px"
								maxH="20px"
								onClick={onClose}
								border="1px solid white"
								borderRadius="50%"
								p="5px"
							>
								<CloseIcon
									w="7px"
									h="7px"
								/>
							</Flex>
						</Flex>
						<Text
							m="10px 0"
							color="#CDCDCD"
							fontSize="12px"
							fontWeight="400"
						>
							{t('user.addUrlInfo').toString()}
						</Text>
					</ModalHeader>
					<ModalBody
						flexWrap="wrap"
						pb="45px"
						color="white"
						gridGap="25px"
						justifyContent="space-between"
						display="flex"
					>
						<Flex
							alignItems="flex-end"
							gridGap="25px"
							flexDir="column"
							justifyContent="space-between"
						>
							<InputGroup
								_focus={{ boxShadow: 'none' }}
								borderColor={Color.BACKGROUND_TERTIARY}
								color="white"
							>
								<InputLeftElement
									pointerEvents="none"
									children={<LinkIcon color="gray_.300" />}
								/>
								<Input
									_focus={{ boxShadow: 'none' }}
									disabled={savingChanges}
									onChange={onInputChange}
									value={ico}
									placeholder="Avatar URL"
								/>
							</InputGroup>
							<Box>
								<Button
									onClick={handleClose}
									disabled={errIco || loadingIco || data.avatar === ico || savingChanges}
								>
									{t('default.close').toString()}
								</Button>
							</Box>
						</Flex>
						<Box
							w="112px"
							h="112px"
							pos="relative"
						>
							<Image
								borderRadius="5px"
								onLoad={onLoad}
								src={ico}
								display={errIco ? 'none !important' : 'block'}
								onError={onErr}
							/>
							{(errIco || loadingIco) && (
								<Flex
									bg={Color.BACKGROUND_LIGHT}
									top="0"
									position="absolute"
									w="100%"
									h="100%"
									justifyContent="center"
									border={`1px solid ${Color.BACKGROUND_TERTIARY}`}
									borderRadius="5px"
									alignItems="center"
									flexDirection="column"
								>
									{loadingIco ? <Spinner /> : <XoctagonIcon />}
									<Text
										mt="6px"
										fontSize="12px"
										color="white"
										fontWeight="400"
									>
										URL
									</Text>
								</Flex>
							)}
						</Box>
					</ModalBody>
				</ModalContent>
			</Modal>

			<IconAvatar
				isOwner={isOwner}
				onClick={onOpenChange}
				width={{ base: '70px', hashup: '190px' }}
				height={{ base: '70px', hashup: '190px' }}
				borderRadius={{ base: '6px', hashup: '18px' }}
				src={editorUserData.avatar ?? data.avatar}
				name={(editorUserData.nickname ?? data.nickname).slice(0, 1)}
				bg={editorUserData.color ?? data.color}
				color={contrastColors(editorUserData.color ?? data.color, '#ffffff') > 3.1 ? 'white' : 'black'}
			/>
		</>
	);
};
